import { ReactNode, createContext, useCallback, useMemo, useState } from 'react';

export type BreadcrumbLevel = {
  level: number;
  data: {
    name: string;
    href?: string;
    component?: ReactNode;
  };
};

export type BreadcrumbItem = {
  label: string;
  href?: string;
  component?: ReactNode;
};

type BreadcrumbContextType = {
  register: (level: number, data: BreadcrumbLevel['data']) => void;
  unregister: (level: number) => void;
  breadcrumbs: BreadcrumbItem[];
};

export const BreadcrumbContext = createContext<BreadcrumbContextType | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [levels, setLevels] = useState<BreadcrumbLevel[]>([]);

  const register = useCallback((level: number, data: BreadcrumbLevel['data']) => {
    setLevels((previousLevels) => {
      const filtered = previousLevels.filter((breadcrumbLevel) => breadcrumbLevel.level !== level);
      return [...filtered, { level, data }];
    });
  }, []);

  const unregister = useCallback((level: number) => {
    setLevels((previousLevels) => previousLevels.filter((breadcrumbLevel) => breadcrumbLevel.level !== level));
  }, []);

  // Memoize breadcrumbs to prevent unnecessary recalculations
  const breadcrumbs = useMemo(() => {
    const sortedLevels = [...levels].sort((a, b) => a.level - b.level);
    return sortedLevels.map((level, index) => ({
      label: level.data.name,
      href: index === sortedLevels.length - 1 ? undefined : level.data.href,
      component: level.data.component,
    }));
  }, [levels]);

  return (
    <BreadcrumbContext.Provider value={{ register, unregister, breadcrumbs }}>{children}</BreadcrumbContext.Provider>
  );
}
